import { useState } from "react";
import { Link } from "react-router-dom";

import { DayEvents, MonthGrid, PlatformLegend, monthName } from "../components/Calendar";
import {
  PLATFORM_NAMES,
  WEEKDAYS,
  addDays,
  eventsOn,
  sameDay,
  startOfDay,
  startOfWeek,
  timeOf,
  useCalendarEvents,
  type CalendarEvent,
} from "../lib/calendar";

type View = "day" | "week" | "month";

/** Every meeting on a calendar: a day, a week or a month at a time, each in
 * its platform's colour. Past meetings link to their recording. */
export function CalendarPage() {
  const { events, isLoading, error } = useCalendarEvents();
  const [view, setView] = useState<View>("week");
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));

  function move(step: number) {
    if (view === "day") setAnchor(addDays(anchor, step));
    else if (view === "week") setAnchor(addDays(anchor, 7 * step));
    else setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + step, 1));
  }

  let title: string;
  if (view === "month") title = monthName(anchor);
  else if (view === "week") {
    const monday = startOfWeek(anchor);
    const sunday = addDays(monday, 6);
    title = `${monday.toLocaleDateString(undefined, { day: "numeric", month: "short" })} – ${sunday.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`;
  } else title = anchor.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="page-body calendar-page">
      <header className="calendar-bar">
        <h1>{title}</h1>
        <div className="cal-nav">
          <button className="btn-quiet" onClick={() => move(-1)} aria-label={`Previous ${view}`}>‹</button>
          <button className="btn-quiet" onClick={() => setAnchor(startOfDay(new Date()))}>Today</button>
          <button className="btn-quiet" onClick={() => move(1)} aria-label={`Next ${view}`}>›</button>
        </div>
        <div className="segmented" role="tablist" aria-label="Calendar view">
          {(["day", "week", "month"] as View[]).map((name) => (
            <button key={name} role="tab" aria-selected={view === name} className={view === name ? "on" : ""}
                    onClick={() => setView(name)}>
              {name[0].toUpperCase() + name.slice(1)}
            </button>
          ))}
        </div>
      </header>
      <PlatformLegend />
      {error && <div className="error">{error.message}</div>}
      {isLoading && <p className="dash-empty">Loading…</p>}

      {view === "month" && (
        <div className="card">
          <MonthGrid
            events={events}
            month={anchor}
            selected={null}
            full
            onSelect={(day) => {
              setAnchor(day);
              setView("day");
            }}
          />
        </div>
      )}
      {view === "week" && <WeekView events={events} anchor={anchor} onPickDay={(day) => { setAnchor(day); setView("day"); }} />}
      {view === "day" && (
        <div className="card">
          <DayEvents day={anchor} events={events} empty="No meetings this day." />
        </div>
      )}
    </div>
  );
}

/** Seven columns, Monday first, with each day's meetings as blocks in time order. */
function WeekView({ events, anchor, onPickDay }: {
  events: CalendarEvent[];
  anchor: Date;
  onPickDay: (day: Date) => void;
}) {
  const monday = startOfWeek(anchor);
  const today = new Date();
  return (
    <div className="week card">
      {WEEKDAYS.map((name, i) => {
        const day = addDays(monday, i);
        const list = eventsOn(events, day);
        return (
          <div key={name} className={`week-day${sameDay(day, today) ? " today" : ""}`}>
            <button className="week-head" onClick={() => onPickDay(day)}>
              <span>{name}</span>
              <strong>{day.getDate()}</strong>
            </button>
            <div className="week-events">
              {list.length === 0 && <span className="week-free">—</span>}
              {list.map((event) => (
                <WeekBlock key={event.key} event={event} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function WeekBlock({ event }: { event: CalendarEvent }) {
  const body = (
    <>
      <span className="block-time">{timeOf(event.start)}–{timeOf(event.end)}</span>
      <span className="block-title">{event.title}</span>
      <span className="block-meta">
        {event.platform ? PLATFORM_NAMES[event.platform] : "Meeting"}
        {event.upcoming && event.state === "cancelled" ? " · not recording" : ""}
      </span>
    </>
  );
  const className = `block p-${event.platform ?? "none"}${event.upcoming ? "" : " past"}`;
  return event.sessionId ? (
    <Link className={className} to={`/meetings/${event.sessionId}`} title={event.label}>{body}</Link>
  ) : (
    <div className={className} title={event.upcoming ? "Coming up" : event.label}>{body}</div>
  );
}
